import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useCallback, useEffect, useRef, useState } from "react";
import { setColumnWidth } from "../../../helpers/cookieUtils";
import ListingHeader from "../listingHeader/ListingHeader";
import {
  Tooltip,
  TableCellLayout,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  makeStyles,
} from "@fluentui/react-components";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import useNavigation from "../../../utils/useNavigation";
import {
  AttachementIcon,
  DeleteIcon,
  EditIcon,
  FlagIcon,
  MeatBallsMenuIcon,
  PendingDeleteIcon,
  RemindIcon,
  DismissCircleIcon,
  CheckmarkCircleIcon
} from "../../../modules/default-svg";
import {
  getAccountCurrency,
  dateOnlyFormatted,
  displayDecimalFormatted,
  isDateGreaterThan,
} from "../../../utils/systemSettings";
import userService from "../../../store/services/user.service";
import scopeService from "../../../store/services/scope.service";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { generalActions } from "../../../store/actions";
import CustomSelect from "../select/CustomSelect";
import Spin from "../spinner/Spin";
import clientService from "../../../store/services/clients.service";
import { apiCall } from "../../../store/services/service";
import {
  changeRequestPriorities,
  compareArrays,
  getDeletePermission,
  getEditPermission,
  getCreatePermission,
  getLabelByType,
  ScopeStatus,
  sortColumnsByOrderlist,
} from "../../../helpers/utils";
import { BASE_URL } from "../../../store/services/URL";
import {
  createScopeRequest,
  updateScopeFlag,
  updateScopeRequest,
} from "../../../store/actions/scope.actions";
import ScopeTaskTable from "./ScopeTasksTable";
import {
  isValidDate,
  localDateToISODateString,
} from "../../../utils/systemSettings";
import { Copy, Layers, Layers2, Target, TriangleAlert } from "lucide-react";
import { createSkeletonRows, TABLE_ACTIONS, useTableContextChange } from "./utils/tableUtils";
import { MilestoneIcon, UnActiveMilestoneIcon } from "../../../views/dashboard/icons/icons";

const getPriority = (priority) => {
  switch (priority) {
    case 0:
      return "Low";
    case 1:
      return "Moderate";
    case 2:
      return "High";
    default:
      return "";
  }
};

const getPriorityPill = (priority) => {
  switch (priority) {
    case 0:
      return "pill OnTrack";
    case 1:
      return "pill OffTrack";
    case 2:
      return "pill AtRisk";
    default:
      return "";
  }
};

const setRequestPriority = (type) => {
  switch (type) {
    case "Low":
      return 0;
    case "Moderate":
      return 1;
    case "High":
      return 2;
    default:
      return 0;
  }
};

const EditInput = ({
  label,
  value,
  onChange,
  type,
  onBlur,
  dateFormat,
  clientId,
  programId,
  portfolioId,
  parentType,
  table,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [resources, setResources] = useState([]);
  const [clients, setClients] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [dropdownOptions, setDropdownOptions] = useState([]);

  const formatValue = (val) => {
    if (!val) return null;
    if (val.label && val.value) return val;
    return { label: val.name, value: val.id };
  };

  const handleDateChange = (newDate) => {
    if (isValidDate(newDate)) {
      const newValue = localDateToISODateString(newDate);
      setInputValue(newValue);
      onChange(newValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const creatingRowState = table.getState().creatingRow;
      if (table?.options?.onCreatingRowSave && creatingRowState) {
        const currentValues = {
          ...creatingRowState.values,
        };
        table.options.onCreatingRowSave({
          values: currentValues,
          table,
        });
      }
    }
  };

  const handleSelectChange = (item) => {
    if (!item) return;
    const newValue = { id: item.value, name: item.label };
    handleInputChange(newValue);
  };

  const handleInputChange = (newValue) => {
    setInputValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const searchHandler = (value) => {
    if (value === "") {
      setResources([]);
      return;
    }
    userService
      .searchUserDataRequest(value)
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          label: obj.name,
          value: obj.id,
        }));
        setResources(transformedArray);
      })
      .catch(() => {
        setResources([]);
      });
  };

  const searchClientHandler = (value) => {
    if (value === "") {
      setClients([]);
      return;
    }
    clientService
      .searchClientsRequest(value)
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          label: obj.name,
          value: obj.id,
        }));
        setClients(transformedArray);
      })
      .catch(() => {
        setClients([]);
      });
  };

  const searchProjectHandler = (value) => {
    if (value === "") {
      setProjectOptions([]);
      return;
    }
    const payload = { searchText: value };
    let queryParam = `?searchTerm=${payload.searchText}&clientId=${clientId}`;
    payload.parent = "Budget";

    if (parentType === "Program") {
      queryParam = queryParam + `&programId=${programId}`;
    } else if (parentType === "Portfolio") {
      queryParam = queryParam + `&portfolioId=${portfolioId}`;
    }

    apiCall
      .get({ url: `${BASE_URL}search/Projects${queryParam}` })
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          start: obj.startDate,
          end: obj.endDate,
          label: obj.name,
          value: obj.id,
        }));
        setProjectOptions(transformedArray);
      })
      .catch(() => {
        setProjectOptions([]);
      });
  };

  const dropdownHandler = () => {
    if (label === "Status") {
      setDropdownOptions(() =>
        ScopeStatus.map((obj) => ({
          label: obj.name,
          value: obj.value,
        }))
      );
    } else if (label === "Priority") {
      setDropdownOptions(() =>
        changeRequestPriorities.map((obj) => ({
          label: obj.name,
          value: obj.value,
        }))
      );
    }
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  if (type === "text") {
    return (
      <input
        placeholder={`Enter ${label}`}
        defaultValue={inputValue}
        type="text"
        onChange={(e) => handleInputChange(e.target.value)}
        className="Custom-Input"
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
      />
    );
  }

  if (type === "number") {
    return (
      <input
        placeholder={`Enter ${label}`}
        defaultValue={inputValue}
        type="number"
        min={0}
        onChange={(e) => handleInputChange(e.target.value)}
        className="Custom-Input"
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
      />
    );
  }

  if (type === "select") {
    let searchTextFunc = searchHandler;
    let options = resources;
    let selectText = "manager";
    let isCreatable = true;
    if (label === "Client") {
      searchTextFunc = searchClientHandler;
      options = clients;
      selectText = "client";
    } else if (label === "Project") {
      searchTextFunc = searchProjectHandler;
      options = projectOptions;
      selectText = "project";
    } else if (label === "Status") {
      searchTextFunc = dropdownHandler;
      options = dropdownOptions;
      selectText = "status";
      isCreatable = false;
    } else if (label === "Priority") {
      searchTextFunc = dropdownHandler;
      options = dropdownOptions;
      selectText = "status";
      isCreatable = false;
    }

    return (
      <div className="d-flex align-items-center w-full max-w-md mx-auto">
        <CustomSelect
          searchText={searchTextFunc}
          SelectText={selectText}
          CustomClass="ModalAvatarSearch CustomSelect"
          options={options}
          value={formatValue(value)}
          onChange={handleSelectChange}
          creatable={isCreatable}
          setCreated={handleSelectChange}
          handleBlur={onBlur}
          isDatatable
          selectWd="w-100"
          onKeyDown={handleKeyDown}
        />
      </div>
    );
  }

  return (
    <DatePicker
      selected={inputValue ? moment(inputValue).toDate() : null}
      onChange={handleDateChange}
      dateFormat={dateFormat}
      className="CustomDatePickerInput"
      onBlur={onBlur}
      popperPlacement="bottom-start"
      placeholderText={dateFormat}
      onKeyDown={handleKeyDown}
      popperClassName="CustomDatePickerPopper"
      showPopperArrow={false}
    />
  );
};

const useStyles = makeStyles({
  tooltip: {
    backgroundColor: "#342c82",
    color: "#FFFFFF",
  },
});

const ScopeTable = (props) => {
  const {
    columnsList,
    rowData,
    setIsCreate,
    searchHandler,
    tableName,
    handleColumnOrdering,
    dispatch,
    setPageNum,
    perPageItems,
    pagination,
    handleReminder,
    handleEdit,
    handleDelete,
    parentType,
    portfolioId,
    programId,
    projectId,
    scopeLoading,
    handleAddRow,
    handleCancelAddRow,
    setRowData,
    isHeader,
    isMilestones = false,
    triggerSearch,
    handleAction
  } = props;
  const styles = useStyles();
  const [cols, setCols] = useState([]);
  const [rows, setRows] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const setting = JSON.parse(localStorage.getItem("settings"));
  const [editedRows, setEditedRows] = useState({});
  const tableContainerRef = useRef(null);
  const lastScrollTop = useRef(0);
  const selectRefs = useRef({});
  const { navigateTo } = useNavigation();
  const [editingHeader, setEditingHeader] = useState(null);
  const [headerValue, setHeaderValue] = useState("");
  const [columnSizing, setColumnSizing] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [isAllRowsExpanded, setIsAllRowsExpanded] = useState(false);
  const [popoverStates, setPopoverStates] = useState({});
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const { hasContextChanged } = useTableContextChange({
    contextValues: {
      portfolioId,
      programId,
      projectId,
      parentType,
    },
    setIsFetchingMore,
    setRows,
    setRowData,
  });

  const handlePopoverChange = (itemId, isOpen) => {
    setPopoverStates(isOpen ? { [itemId]: isOpen } : {});
  };
  const [colsList, setColumnsList] = useState([]);
  useEffect(() => {
    if (columnsList.length) {
      setColumnsList(columnsList);
    }
  }, [columnsList]);
  const handleHeaderDoubleClick = (columnId, currentHeader) => {
    setEditingHeader(columnId);
    setHeaderValue(currentHeader);
  };

  const handleHeaderSave = (columnId) => {
    if (!headerValue.trim()) {
      dispatch(
        generalActions.showToast({
          title: "Header name cannot be empty",
          type: "error",
        })
      );
      return;
    }

    const updatedColumnsDB = cols.map((col) => ({
      name: col.Label,
      customName: col.accessorKey === columnId ? headerValue : col.CustomName,
      order: col.Order,
      isVisible: col.Visibility,
    }));

    const payload = {
      Name: tableName,
      Columns: updatedColumnsDB,
    };

    userService
      .putUserLayoutRequest(payload)
      .then(() => { })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Error updating column header",
            type: "error",
          })
        );
      })
      .finally(() => {
        setColumnsList((prevCols) => {
          const newCols = prevCols.map((col) =>
            col.accessorKey === columnId
              ? { ...col, header: headerValue, customName: headerValue }
              : col
          );
          return newCols;
        });
      });

    setEditingHeader(null);
    setHeaderValue("");
  };

  const handleRedirect = (item) => {
    const entityType = isMilestones ? "Milestones" : "ScopeItems";
    navigateTo(entityType, item.id, item.name);
  };

  const handleProjectRedirect = (item) => {
    if (item.project) {
      navigateTo("Projects", item.project.id, item.project.name);
    }
  };

  const handleFlag = (id, status) => {
    const newData = [...rowData];
    const index = newData.findIndex((item) => item.id === id);
    if (index !== -1) {
      newData[index].isFlagged = status;
    }

    setRowData(
      newData.sort((a, b) => {
        return b.isFlagged - a.isFlagged;
      })
    );
    dispatch(
      updateScopeFlag({
        id: id,
        payload: [
          {
            op: "replace",
            path: "/IsFlagged",
            value: status,
          },
        ],
      })
    );
  };

  const handleToggleField = (id, field, newValue) => {
      const prevData = [...rowData];
      const newData = [...rowData];
      const index = newData.findIndex((item) => item.id === id);
      if (index === -1) return;
      newData[index] = { ...newData[index], [field]: newValue };
      setRowData(newData);
  
      scopeService
        .patchScopeField(id, field, newValue)
        .catch(() => {
          setRowData(prevData);
          dispatch(
            generalActions.showToast({
              title: `Failed to update Scope`,
              type: "error",
            })
          );
        });
    };

  const handleToggleMilestone = (id, newValue) => {
    const prevData = [...rowData];
    const newData = [...rowData];
    const index = newData.findIndex((item) => item.id === id);
    if (index === -1) return;
    newData[index] = { ...newData[index], isMilestone: newValue };
    setRowData(newData);

    scopeService
      .updateScopeMilestoneStatus(id, newValue)
      .catch(() => {
        setRowData(prevData);
        dispatch(
          generalActions.showToast({
            title: "Failed to update Milestone status",
            type: "error",
          })
        );
      });
  };

  const fetchMoreData = useCallback(() => {
    if (!pagination?.HasNext || scopeLoading || isFetchingMore || hasContextChanged) return;
    setIsFetchingMore(true);
    setPageNum(pagination.CurrentPage + 1);
  }, [pagination, scopeLoading, isFetchingMore, setPageNum, hasContextChanged]);

  useEffect(() => {
    if (!scopeLoading && isFetchingMore) {
      setIsFetchingMore(false);
    }
  }, [scopeLoading, rowData.length]);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement) => {
      if (!containerRefElement || scopeLoading || !pagination?.HasNext) return;

      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      const scrolledToBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 50;
      const isScrollingDown = scrollTop > lastScrollTop.current;

      lastScrollTop.current = scrollTop;

      if (isScrollingDown && scrolledToBottom) {
        fetchMoreData();
      }
    },
    [fetchMoreData, scopeLoading, pagination]
  );

  const throttledFetchMore = useCallback(
    (() => {
      let timeoutId;
      return (containerRefElement) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          fetchMoreOnBottomReached(containerRefElement);
        }, 100);
      };
    })(),
    [fetchMoreOnBottomReached]
  );

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      const handleScroll = () => throttledFetchMore(container);
      container.addEventListener("scroll", handleScroll, { passive: true });
      return () => {
        container.removeEventListener("scroll", handleScroll);
      };
    }
  }, [throttledFetchMore]);

  useEffect(() => {
    if (tableContainerRef.current && triggerSearch !== undefined) {
      tableContainerRef.current.scrollTop = 0;
      lastScrollTop.current = 0;
    }
  }, [triggerSearch]);

  const formatPayload = (payload) => {
    const formattedPayload = {};
    Object.keys(payload).forEach((key) => {
      formattedPayload[key] = payload[key];
    });
    return formattedPayload;
  };

  const handleSave = (rowId, payload) => {
    if (projectId) {
      dispatch(
        updateScopeRequest({
          sId: rowId,
          parentID: projectId,
          parent: "Project",
          payload,
        })
      );
      return;
    }
    if (portfolioId) {
      dispatch(
        updateScopeRequest({
          sId: rowId,
          parentID: portfolioId,
          parent: "Portfolio",
          payload,
        })
      );
      return;
    }
    if (programId) {
      dispatch(
        updateScopeRequest({
          sId: rowId,
          parentID: programId,
          parent: "Program",
          payload,
        })
      );
      return;
    }

    dispatch(updateScopeRequest({ sId: rowId, parent: "Scope", payload }));
    setEditedRows({});
    return;
  };

  const handleCreate = (payload) => {
    if (projectId) {
      dispatch(
        createScopeRequest({
          parentID: projectId,
          parent: "Project",
          payload,
          perPageItems,
        })
      );
      return;
    }
    if (programId) {
      dispatch(
        createScopeRequest({
          parentID: programId,
          parent: "Program",
          payload,
          perPageItems,
        })
      );
      return;
    }
    if (portfolioId) {
      dispatch(
        createScopeRequest({
          parentID: portfolioId,
          parent: "Portfolio",
          payload,
          perPageItems,
        })
      );
      return;
    }

    dispatch(createScopeRequest({ parent: "Scope", payload, perPageItems }));
  };

  const getSortingFunction = (col) => {
    switch (col.label) {
      case "Scope":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.name || "").toLowerCase();
          const b = (rowB.original.name || "").toLowerCase();
          return a.localeCompare(b);
        };
      case "Project":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.project?.name || "").toLowerCase();
          const b = (rowB.original.project?.name || "").toLowerCase();
          return a.localeCompare(b);
        };
      case "Phase":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.phase || "").toLowerCase();
          const b = (rowB.original.phase || "").toLowerCase();
          return a.localeCompare(b);
        };
      case "Status":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.status || 0;
          const b = rowB.original.status || 0;
          return a - b;
        };
      case "Priority":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.priority || 0;
          const b = rowB.original.priority || 0;
          return a - b;
        };
      case "Milestone":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.isMilestone ? 1 : 0;
          const b = rowB.original.isMilestone ? 1 : 0;
          return b - a;
        };
      case "In WSR":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.isInWsr ? 1 : 0;
          const b = rowB.original.isInWsr ? 1 : 0;
          return b - a;
        };
      case "Budget":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.budget) || 0;
          const b = parseFloat(rowB.original.budget) || 0;
          return a - b;
        };
      case "Actual":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.actual) || 0;
          const b = parseFloat(rowB.original.actual) || 0;
          return a - b;
        };
      case "Accural":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.accural) || 0;
          const b = parseFloat(rowB.original.accural) || 0;
          return a - b;
        };
      case "ETC":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.etc) || 0;
          const b = parseFloat(rowB.original.etc) || 0;
          return a - b;
        };
      case "BAC":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.bac) || 0;
          const b = parseFloat(rowB.original.bac) || 0;
          return a - b;
        };
      case "Tasks":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.tasksCount) || 0;
          const b = parseFloat(rowB.original.tasksCount) || 0;
          return a - b;
        };
      case "Start Date":
        return (rowA, rowB, columnId) => {
          const a = new Date(rowA.original.startDate || 0);
          const b = new Date(rowB.original.startDate || 0);
          return a.getTime() - b.getTime();
        };
      case "End Date":
        return (rowA, rowB, columnId) => {
          const a = new Date(rowA.original.endDate || 0);
          const b = new Date(rowB.original.endDate || 0);
          return a.getTime() - b.getTime();
        };
      case "%":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.progress) || 0;
          const b = parseFloat(rowB.original.progress) || 0;
          return a - b;
        };
      default:
        return "alphanumeric";
    }
  };

  const getSortDescFirst = (col) => {
    const descFirstColumns = [
      "Milestone",
      "In WSR",
      "Priority",
      "Budget",
      "Actual",
      "Accural",
      "ETC",
      "BAC",
      "Tasks",
      "Start Date",
      "End Date",
      "%",
    ];
    return descFirstColumns.includes(col.label);
  };


  const handleScopeDuplicate = (e, item, isIncludeTasks = false) => {
    e.stopPropagation();
    e.preventDefault();
    handlePopoverChange(
      item.id,
      !popoverStates[item.id]
    );

    if (handleAction) {
      handleAction({
        actionType: TABLE_ACTIONS.DUPLICATE,
        payload: {
          id: item.id,
          name: item.name,
          isIncludeTasks: isIncludeTasks
        }
      });
    }
  };

  useEffect(() => {
    if (!colsList?.length && !rowData.length) return;
    const columnsOrder = colsList
      .filter((col) => col.isVisible !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((item) => item.accessorKey);
    setColumnOrder(["mrt-row-expand", ...columnsOrder]);
    const columns = colsList
      .filter((col) => col.isVisible !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((col, index) => {
        return {
          Label: col.label,
          CustomName: col.customName,
          Order: col.order,
          Visibility: col.isVisible,
          accessorKey: col.accessorKey,
          header: col.header,
          enableHiding: true,
          enableColumnActions: false,
          grow: true,
          enableResizing: true,
          size: col.size,
          editVariant: col.columnType,
          enableGlobalFilter: true,
          enableEditing:
            col.accessorKey === "tasksCount" ||
              col.accessorKey === "actual" ||
              col.accessorKey === "accural" ||
              col.accessorKey === "etc" ||
              col.accessorKey === "bac" ||
              col.accessorKey === "progress"
              ? false
              : true,
          enableSorting: editingHeader === col.accessorKey ? false : true,
          sortingFn: getSortingFunction(col),
          sortDescFirst: getSortDescFirst(col),
          muiEditTextFieldProps: ({ cell, row }) => ({
            sx: {
              fontFamily: "Figtree-Regular",
              fontSize: "14px",
              padding: "0.5rem",
              maxHeight: "28px",
            },
            required: col.required || false,
            error: !!validationErrors?.[`${row.id}_${cell.column.id}`],
            helperText: validationErrors?.[`${row.id}_${cell.column.id}`],
            onFocus: () =>
              setValidationErrors({
                ...validationErrors,
                [cell.column.id]: undefined,
              }),
            inputRef: (el) => {
              if (col.columnType === "select") {
                selectRefs.current[`${row.id}_${cell.column.id}`] = el;
              }
            },
            onChange: (event) => {
              const newValue = event.target.value;
              setEditedRows((prev) => ({
                ...prev,
                [row.id]: {
                  ...prev[row.id],
                  [cell.column.id]: newValue,
                },
              }));
            },
            onBlur: (event) => {
              const value = event.target.value;
              const validationError =
                col.required && !value ? "Required" : undefined;
              setValidationErrors((prev) => ({
                ...prev,
                [`${row.id}_${cell.column.id}`]: validationError,
              }));
            },
          }),
          Cell: ({ cell, row }) => {
            const item = row.original;
            if (item.isSkeleton) {
              return (
                <div
                  style={{
                    height: '20px',
                    background: 'linear-gradient(90deg, #e0e0e0 25%, #ECE7FD 50%, #e0e0e0 75%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.5s infinite',
                    borderRadius: '4px',
                    width: '100%'
                  }}
                />
              );
            }
            switch (col.label) {
              case "Scope":
                return (
                  <div className="d-flex justify-content-between align-items-center inputWidth">
                    <div className="d-flex align-items-center inputWidth">
                      <Tooltip
                        positioning="before"
                        content={{
                          children: item.isFlagged ? "Unflag" : "Flag",
                          className: styles.tooltip,
                        }}
                        relationship="label"
                        withArrow
                      >
                        <span
                          className={`flag ${item.isFlagged ? "flagActive" : ""
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleFlag(item.id, !item.isFlagged);
                          }}
                        >
                          <FlagIcon />
                        </span>
                      </Tooltip>
                      <>
                        <span className="mr-2 min-w-[16px]">
                          {item.risks && item.risks.length > 0 && item.risks.some(r => r.status !== 5) && (
                            <Tooltip content="Scope includes risks that may affect delivery" relationship="label">
                              <TriangleAlert size={16} color="#d1424f" />
                            </Tooltip>
                          )}
                        </span>
                        <span
                          className="d-flex w-full text-truncate cursor-pointer"
                          onClick={() => handleRedirect(item)}
                        >
                          {item.attachementsCount > 0 && (
                            <div className="mr-1">
                              <AttachementIcon count={item.attachementsCount} />
                            </div>
                          )}
                          {item.isPendingDelete && (
                            <div className="mr-1">
                              <PendingDeleteIcon />
                            </div>
                          )}
                          {item.name}
                        </span>
                      </>
                      <div className="meatballs-trigger">
                        <Popover
                          open={popoverStates[item.id]}
                          onOpenChange={(e, data) => handlePopoverChange(item.id, data.open || false)}
                          positioning="below-start"
                        >
                          <PopoverTrigger disableButtonEnhancement>
                            <TableCellLayout
                              media={<MeatBallsMenuIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handlePopoverChange(
                                  item.id,
                                  !popoverStates[item.id]
                                );
                              }}
                            />
                          </PopoverTrigger>
                          <PopoverSurface
                            tabIndex={-1}
                            className="listingPopoverWrapper"
                          >
                            <div className="listingPopoverContent">
                              {getEditPermission("Scope") && (
                                <div
                                  className="popItem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleEdit(item);
                                    handlePopoverChange(
                                      item.id,
                                      !popoverStates[item.id]
                                    );
                                  }}
                                >
                                  <EditIcon />
                                  <p className="text-sm-400 ms-12">Edit</p>
                                </div>
                              )}
                              {getCreatePermission("Scope") && (
                                item.tasksCount === 0 ? (
                                  <div
                                    className="popItem"
                                    onClick={(e) => { handleScopeDuplicate(e, item) }}
                                  >
                                    <Copy size={16} />
                                    <p className="text-sm-400 ms-12">Duplicate</p>
                                  </div>
                                ) : (
                                  <Popover positioning="after-top" trapFocus>
                                    <PopoverTrigger disableButtonEnhancement>
                                      <div className="popItem">
                                        <Copy size={16} />
                                        <p className="text-sm-400 ms-12">Duplicate</p>
                                      </div>
                                    </PopoverTrigger>
                                    <PopoverSurface className="listingPopoverWrapper">
                                      <div className="listingPopoverContent">
                                        <div
                                          className="popItem"
                                          onClick={(e) => { handleScopeDuplicate(e, item) }}
                                        >
                                          <Target size={16} />
                                          <p className="text-sm-400 ms-12">{"Scope"}</p>
                                        </div>
                                        <div
                                          className="popItem"
                                          onClick={(e) => { handleScopeDuplicate(e, item, true) }}
                                        >
                                          <Layers2 size={16} />
                                          <p className="text-sm-400 ms-12">{"Scope & tasks"}</p>
                                        </div>
                                      </div>
                                    </PopoverSurface>
                                  </Popover>
                                )
                              )}
                              <div
                                className="popItem"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleReminder(item);
                                  handlePopoverChange(
                                    item.id,
                                    !popoverStates[item.id]
                                  );
                                }}
                              >
                                <RemindIcon />
                                <p className="text-sm-400 ms-12">Remind me</p>
                              </div>
                              {getDeletePermission("Scope") && (
                                <div
                                  className="popItem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handlePopoverChange(
                                      item.id,
                                      !popoverStates[item.id]
                                    );
                                    handleDelete(item);
                                  }}
                                >
                                  <DeleteIcon />
                                  <p className="text-sm-400 ms-12">Delete</p>
                                </div>
                              )}
                            </div>
                          </PopoverSurface>
                        </Popover>
                      </div>
                    </div>
                  </div>
                );
                return (
                  <div className="d-flex justify-content-between inputWidth">
                    <div className="d-flex align-items-center inputWidth">
                      <Tooltip
                        positioning="before"
                        content={{
                          children: item.isFlagged ? "Unflag" : "Flag",
                          className: styles.tooltip,
                        }}
                        relationship="label"
                        withArrow
                      >
                        <span
                          className={`flag ${item.isFlagged ? "flagActive" : ""
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleFlag(item.id, !item.isFlagged);
                          }}
                        >
                          <FlagIcon />
                        </span>
                      </Tooltip>
                      <span className="mr-2 min-w-[16px]">
                        {item.risks && item.risks.length > 0 && item.risks.some(r => r.status !== 5) && (
                          <Tooltip content="Milestone includes risks that may affect delivery" relationship="label">
                            <TriangleAlert size={16} color="#d1424f" />
                          </Tooltip>
                        )}
                      </span>
                      <span
                        className="d-flex w-full text-truncate"
                        onClick={() => handleRedirect(item)}
                      >
                        {item.name}
                      </span>
                    </div>
                    <div className="meatballs-trigger">
                      <Popover
                        open={popoverStates[item.id]}
                        onOpenChange={(isOpen) =>
                          handlePopoverChange(item.id, isOpen)
                        }
                        positioning="below-start"
                      >
                        <PopoverTrigger disableButtonEnhancement>
                          <TableCellLayout
                            media={<MeatBallsMenuIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handlePopoverChange(
                                item.id,
                                !popoverStates[item.id]
                              );
                            }}
                          />
                        </PopoverTrigger>
                        <PopoverSurface
                          tabIndex={-1}
                          className="listingPopoverWrapper"
                        >
                          <div className="listingPopoverContent">
                            <div
                              className="popItem"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleEdit(item);
                                handlePopoverChange(
                                  item.id,
                                  !popoverStates[item.id]
                                );
                              }}
                            >
                              <EditIcon />
                              <p className="text-sm-400 ms-12">Edit</p>
                            </div>
                            <div
                              className="popItem"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleReminder(item);
                                handlePopoverChange(
                                  item.id,
                                  !popoverStates[item.id]
                                );
                              }}
                            >
                              <RemindIcon />
                              <p className="text-sm-400 ms-12">Remind me</p>
                            </div>
                            <div
                              className="popItem"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handlePopoverChange(
                                  item.id,
                                  !popoverStates[item.id]
                                );
                                handleDelete(item);
                              }}
                            >
                              <DeleteIcon />
                              <p className="text-sm-400 ms-12">Delete</p>
                            </div>
                          </div>
                        </PopoverSurface>
                      </Popover>
                    </div>
                  </div>
                );
              case "Project":
                return (
                  <span onClick={() => handleProjectRedirect(item)}>
                    {item.project ? item.project.name : ""}
                  </span>
                );
              case "Phase":
                return item.phase;
              case "Status":
                return (
                  <div className="flex w-100 justify-center items-center">
                    <p
                      className={`pill w-100 ${item.status === 0
                        ? "OnTrack"
                        : item.status === 1
                          ? "OffTrack"
                          : item.status === 2
                            ? "AtRisk"
                            : "Completed"
                        }`}
                    >
                      {getLabelByType("scope", item.status)}
                    </p>
                  </div>
                );
              case "Priority":
                return (
                  <div className="flex w-100 justify-center items-center">
                    <p className={`${getPriorityPill(item.priority)} w-100`}>
                      {getPriority(item.priority)}
                    </p>
                  </div>
                );
              case "Milestone":
                return (

                  <div
                    className="flex w-100 justify-center items-center cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleMilestone(item.id, !item.isMilestone);
                    }}
                  >

                    {item.isMilestone ? (
                      <MilestoneIcon size={20} />
                    ) : (
                      <Tooltip
                        positioning="before"
                        content={{
                          children: "Mark as Milestone",
                          className: styles.tooltip,
                        }}
                        relationship="label"
                        withArrow
                      >
                        <span className="milestone-inactive">
                          <UnActiveMilestoneIcon size={20} />
                        </span>
                      </Tooltip>
                    )}
                  </div >

                );
              case "In WSR":
                return (
                  <div
                    className="flex w-100 justify-center items-center cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleField(item.id, "isInWsr", !item.isInWsr);
                    }}
                  >
                    {item.isInWsr ? (
                      <CheckmarkCircleIcon />
                    ) : (
                      <DismissCircleIcon />
                    )}
                  </div>
                );
              case "Budget":
                return `${getAccountCurrency(
                  setting
                )} ${displayDecimalFormatted(setting, item.budget)}`;
              case "Actual":
                return item.actual
                  ? `${getAccountCurrency(setting)} ${displayDecimalFormatted(
                    setting,
                    item.actual
                  )}`
                  : "";
              case "Accural":
                return item.accural
                  ? `${getAccountCurrency(setting)} ${displayDecimalFormatted(
                    setting,
                    item.accural
                  )}`
                  : "";
              case "ETC":
                return item.etc
                  ? `${getAccountCurrency(setting)} ${displayDecimalFormatted(
                    setting,
                    item.etc
                  )}`
                  : "";
              case "Tasks":
                return `${displayDecimalFormatted(setting, item.tasksCount)}`;
              case "Start Date":
                return item.startDate
                  ? dateOnlyFormatted(setting, item.startDate)
                  : "";
              case "End Date":
                return item.endDate
                  ? dateOnlyFormatted(setting, item.endDate)
                  : "";
              case "%":
                return item.progress ? `${item.progress}%` : "";
              default:
                return cell.getValue();
            }
          },
          Edit: ({ cell, row, table }) => {
            const value =
              editedRows[row.id]?.[cell.column.id] ??
              cell.getValue() ??
              row.original[cell.column.id] ??
              (col.columnType === "select" ? null : "");
            return (
              <EditInput
                label={col.label}
                value={value}
                onChange={(newValue) => {
                  if (row.id.startsWith("mrt-row-create")) {
                    table.setCreatingRow((prev) => ({
                      ...prev,
                      values: {
                        ...prev.values,
                        [cell.column.id]: newValue,
                      },
                    }));
                  } else {
                    setEditedRows((prev) => ({
                      ...prev,
                      [row.id]: {
                        ...prev[row.id],
                        [cell.column.id]: newValue,
                      },
                    }));
                  }
                }}
                onBlur={() => {
                  if (!row.id.startsWith("mrt-row-create")) {
                    const newValue = editedRows[row.id]?.[cell.column.id];
                    table.options.onEditingCellSave?.({
                      cell,
                      value: newValue,
                      row,
                      table,
                    });
                    table.setEditingCell(null);
                  }
                }}
                type={col.columnType}
                dateFormat={
                  col.columnType === "date"
                    ? setting.regionalSettings.dateFormat
                    : null
                }
                clientId={row.original?.client?.id}
                portfolioId={row.original?.portfolio?.id}
                programId={row.original?.program?.id}
                parentType={parentType}
                table={table}
              />
            );
          },
          Header: ({ column }) => {
            return editingHeader === column.id ? (
              <input
                value={headerValue}
                onChange={(e) => setHeaderValue(e.target.value)}
                onBlur={() => handleHeaderSave(column.id)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleHeaderSave(column.id);
                  }
                }}
                autoFocus
                className="header-edit-input"
                style={{
                  minWidth: column.getSize() - 60,
                }}
              />
            ) : (
              <span>{column.columnDef.header}</span>
            );
          },
        };
      });
    setCols((prevCols) => {
      if (JSON.stringify(prevCols) !== JSON.stringify(columns)) {
        return columns;
      }
      return prevCols;
    });

    setRows(() => {
      if (!rowData || !Array.isArray(rowData) || rowData.length === 0) {
        return [];
      }
      let baseRows = rowData;
      if (isMilestones) {
        baseRows = rowData.filter((item) => item.isMilestone);
      }

      if (isFetchingMore && pagination?.HasNext) {
        const skeletonRows = createSkeletonRows();
        return [...baseRows, ...skeletonRows];
      }

      return baseRows;
    });
  }, [colsList, rowData, editingHeader, headerValue, isFetchingMore, pagination?.HasNext, isMilestones]);


  const TaskDetailPanel = ({ projectId, scopeId, scopeName, dispatch }) => {
    const [tasks, setTasks] = useState([]);
    const [tempFiltredtasks, setTempFiltredtasks] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchTasks = async () => {
      try {
        setLoading(true);
        await scopeService.taskRequest({
          id: scopeId,
          parent: "Scope",
        }).then((response) => {
          return setTasks(response.data);
        });
      } catch (error) {
        dispatch(
          generalActions.showNotificationToast({
            title: "Failed to fetch tasks",
            type: "error",
          })
        );
        setTasks([]);
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      fetchTasks();
    }, [scopeId, dispatch]);

    if (loading) {
      return <div className="p-4 text-center w-full">Loading tasks...</div>;
    }

    const handleSearchTasks = (value) => {
      if (!value)
        setTempFiltredtasks(null);
      else
        setTempFiltredtasks(tasks.filter((task) => task.name.toLowerCase().includes(value)));
    };

    return (
      <ScopeTaskTable
        rowData={tempFiltredtasks || tasks}
        searchHandler={() => { }}
        tableName="Scope-Tasks"
        dispatch={dispatch}
        scopeId={scopeId}
        scopeName={scopeName}
        portfolioId={portfolioId}
        programId={programId}
        projectId={projectId}
        setRowData={setTasks}
        triggerSearchTasks={handleSearchTasks}
      />
    );
  };

  const renderDetailPanel = useCallback(
    ({ row }) => (
      <div
        style={{
          padding: "0 0.5rem",
          borderLeft: "2px solid #342c82",
          backgroundColor: "#f9f9fb",
          width: "100%",
        }}
      >
        <TaskDetailPanel
          scopeId={row.original.id}
          scopeName={row.original.name}
          projectId={row.original.project.id}
          dispatch={dispatch}
        />
      </div>
    ),
    [dispatch]
  );

  const table = useMaterialReactTable({
    columns: cols,
    data: rows,
    ...(!isMilestones && {
      enableExpanding: (row) => row.original.tasksCount > 0,
    }),
    ...(!isMilestones && {
      getRowCanExpand: (row) => row.original.tasksCount > 0,
    }),
    ...(Object.keys(expandedRows).length > 0 && {
      renderDetailPanel: renderDetailPanel,
    }),
    initialState: {
      expanded: expandedRows,
      density: "compact",
      showGlobalFilter: false,
      showColumnFilters: false,
      enableHiding: false,
      columnOrder,
    },
    state: {
      sorting,
      isLoading: rows.length === 0 && scopeLoading,
      columnSizing,
      columnOrder,
      showLoadingOverlay: false,
      showProgressBars: false,
      expanded: expandedRows,
    },
    enableHiding: true,
    enableSorting: true,
    manualSorting: false,
    onSortingChange: setSorting,
    visibleInShowHideMenu: false,
    enablePagination: false,
    enableTopToolbar: true,
    enableGlobalFilter: true,
    enableStickyHeader: true,
    enableColumnFilterModes: true,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    editDisplayMode: "cell",
    globalFilterFn: "contains",
    enableEditing: false,
    enableFilters: false,
    enableCreatingRow: true,
    createDisplayMode: "row",
    positionCreatingRow: "bottom",
    muiExpandButtonProps: ({ row, table }) => ({
      onClick: (e) => {
        e.stopPropagation();
        const isCurrentlyExpanded = expandedRows[row.id];
        setExpandedRows((prev) => ({
          ...prev,
          [row.id]: !isCurrentlyExpanded,
        }));
      },
    }),
    muiExpandAllButtonProps: () => ({
      onClick: (e) => {
        e.stopPropagation();
        setExpandedRows(!isAllRowsExpanded ? rows.map((row) => ({ [row.id]: true })) : []);
        setIsAllRowsExpanded(!isAllRowsExpanded);
      },
    }),
    onCreatingRowCancel: () => {
      setValidationErrors({});
      handleCancelAddRow();
    },
    onCreatingRowSave: ({ values, table }) => {
      const errors = {};
      if (
        values?.startDate &&
        values?.endDate &&
        isDateGreaterThan(values?.startDate, values?.endDate)
      ) {
        errors[
          "endDate"
        ] = `End date must be equal to or greater than start date.`;
      }
      if (
        values?.startDate &&
        values?.revisedEndDate &&
        isDateGreaterThan(values?.startDate, values?.revisedEndDate)
      ) {
        errors[
          "revisedEndDate"
        ] = `Revised end date must be equal to or greater than start date.`;
      }
      if (!values?.name) errors["name"] = `The Name field is required.`;

      if (!projectId) {
        if (!projectId) errors["projectId"] = `The Project field is required.`;
      }

      if (Object.keys(errors).length) {
        dispatch(
          generalActions.showToast({
            title:
              errors.name ||
              errors.projectId ||
              errors.endDate ||
              errors.revisedEndDate,
            type: "error",
          })
        );
        return;
      }

      const payload = {
        Name: values?.name,
        ProjectId: projectId ? projectId : "",
        StartDate: values?.startDate ? values?.startDate : null,
        EndDate: values?.endDate ? values?.endDate : null,
        RevisedEndDate: values?.revisedEndDate ? values?.revisedEndDate : null,
        Priority: setRequestPriority(values?.priority),
        Budget: values?.budget ? values?.budget : 0,
        Phase: values?.phase ?? "",
        Status: values?.status ?? 0,
        RefId: values?.refId ?? "",
        Description: values?.description ?? "",
        AssignedUsers: [],
        IsMilestone: false,
      };
      handleCreate(payload);
      table.setCreatingRow(null);
    },
    onEditingRowCancel: () => {
      setValidationErrors({});
      setEditedRows({});
    },
    positionGlobalFilter: "left",
    onEditingCellSave: ({ cell, row }) => {
      const editedRow = editedRows[row.id];
      const newValue =
        editedRow?.[cell.column.id] !== undefined
          ? editedRow[cell.column.id]
          : cell.getValue();

      if (newValue === cell.getValue()) return; // Skip if no real change
      setRows((prevRows) =>
        prevRows.map((r, index) => {
          if (index === row.index) {
            if (cell.column.id === "status") {
              return {
                ...r,
                status: ScopeStatus.find((item) => item.name === newValue.name)
                  ?.value,
              };
            } else if (cell.column.id === "priority") {
              return { ...r, priority: setRequestPriority(newValue.name) };
            }
            return { ...r, [cell.column.id]: newValue };
          }
          return r;
        })
      );

      // Remove the specific cell edit
      setEditedRows((prev) => {
        const updated = { ...prev };
        if (updated[row.id]) {
          delete updated[row.id][cell.column.id];
          if (Object.keys(updated[row.id]).length === 0) {
            delete updated[row.id];
          }
        }
        return updated;
      });

      const prePayload =
        cell.column.id === "owner"
          ? {
            ...row.original,
            OwnerId: newValue?.id,
          }
          : cell.column.id === "status"
            ? {
              ...row.original,
              status: ScopeStatus.find((item) => item.name === newValue.name)
                ?.value,
            }
            : cell.column.id === "priority"
              ? {
                ...row.original,
                priority: setRequestPriority(newValue.name),
              }
              : {
                ...row.original,
                [cell.column.id]: newValue,
              };
      const payload = formatPayload(prePayload);
      handleSave(row.original.id, {
        ...payload,
        ClientId: payload.client.id,
        ProjectId: payload.project.id,
      });
    },
    onColumnSizingChange: (updater) => {
      setColumnSizing((prev) => {
        const newSizing =
          typeof updater === "function" ? updater(prev) : updater;
        Object.entries(newSizing).forEach(([columnId, width]) => {
          setColumnWidth(tableName, columnId, width);
        });
        return newSizing;
      });
    },
    onColumnOrderChange: (updater) => {
      const newOrder = typeof updater === "function" ? updater() : updater;
      const neworderList = newOrder
        .filter((item) => item !== "mrt-row-spacer")
        .filter(Boolean);
      const areEqual = compareArrays(
        neworderList
          .filter((item) => item !== "mrt-row-expand")
          .filter(Boolean),
        columnOrder.filter((item) => item !== "mrt-row-expand")
      );
      if (areEqual) {
        return neworderList;
      }

      const sortedColumns = sortColumnsByOrderlist(colsList, neworderList);
      handleColumnOrdering(sortedColumns);
      setColumnOrder(neworderList);
      return neworderList;
    },
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        fontFamily: "Figtree-Medium",
        fontSize: "14px",
        padding: "1rem 0.5rem 1rem 1rem",
        backgroundColor: "#f5f5f5",
        height: "40px",
        zIndex: 100,
        fontWeight: 600,
        "&:hover": {
          backgroundColor: "#ece8fd",
        },
        position: "relative",
        "& .Mui-TableHeadCell-Content": {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          height: "100%",
        },
        "& .MuiTableSortLabel-root": {
          marginLeft: "8px",
          paddingTop: "3px",
        },
        ...(column.id === "mrt-row-expand" && {
          maxWidth: "40px",
          width: "40px",
          minWidth: "40px",
          padding: "1rem 0.5rem ",
        }),
      },
      onDoubleClick: (e) => {
        e.stopPropagation();
        e.preventDefault();
        handleHeaderDoubleClick(column.id, column.columnDef.header);
      },
    }),
    muiTopToolbarProps: {
      showColumnFilters: false,
      showGlobalFilter: true,
      enableHiding: false,
      sx: {
        position: "sticky",
        top: 0,
        zIndex: 1,
        "& .MuiBox-root": {
          padding: 0,
          display: "flex",
          alignItems: "center",
        },
      },
    },
    renderTopToolbar: () => (
      <ListingHeader
        isHeader={isHeader}
        parentType={parentType}
        type="Scope"
        btnLable={!isMilestones ? "Add Scope" : "Add Milestone"}
        searchedData={searchHandler}
        setIsCreate={() => setIsCreate(true)}
        rightPane={true}
        portfolioId={portfolioId}
        programId={programId}
        projectId={projectId}
        isCreatable={true}
        isSearchDisable={cols.length}
        handleCreateFunc={() => {
          handleAddRow();
          table.setCreatingRow(true);
        }}
        triggerSearch={triggerSearch}
      />
    ),
    renderBottomToolbarCustomActions: () => (
      <div className="bottom-toolbar-container">
        <div className="toolbar-content">
          {scopeLoading || isFetchingMore ? (
            <div className="loading-indicator">
              <span>{`${!isFetchingMore ? 'Loading...' : 'Loading more data...'}`}</span>
            </div>
          ) : pagination?.TotalCount > 0 ? (
            <div className="data-info">
              <span className="data-text">
                Showing <strong>{rows.length}</strong> of{" "}
                <strong>{pagination?.TotalCount || 0}</strong> total records
              </span>
            </div>
          ) : (
            <div className="data-info">
              <span className="data-text">No records Found</span>
            </div>
          )}
        </div>
      </div>
    ),
    muiBottomToolbarProps: {
      sx: {
        position: "absolute",
        bottom: 0,
        backgroundColor: "#f5f5f5",
        padding: "0.7rem 1rem",
        width: "100%",
        height: "38px",
      },
    },
    muiTableBodyProps: {
      sx: {
        fontFamily: "Figtree-Regular",
        fontSize: "13px",

        marginBottom: "66px",
        "& .MuiTableBody-Cell": {
          "&:hover": {
            backgroundColor: "#ece8fd15",
          },
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        ...(expandedRows[row.id]
          ? {
            backgroundColor: "#ece8fd",
            "& td": {
              verticalAlign: "top",
              backgroundColor: "#transparent !important",
            },
          }
          : {
            "& td": {
              backgroundColor: "transparent",
            },
          }),
        ...(isMilestones && { height: "32px" }),
        ...(row.original.isSkeleton && {
          "&:hover": {
            backgroundColor: "transparent",
          },
          cursor: "default",
        }),
      },
      className: row.original.isSkeleton ? "" : "hoverable-row",
    }),
    muiDetailPanelProps: () => ({
      sx: {
        backgroundColor: "#f8f9fa",
        borderTop: "1px solid #dee2e6",
        "& td": {
          padding: 0,
        },
      },
    }),
    muiTableContainerProps: ({ table }) => ({
      ref: tableContainerRef,
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight || 115
          }px)`,
        overflow: "auto",
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#FFFFFF",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#342c82",
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: "#342c82",
          },
        },
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiSkeletonProps: {
      height: 34,
      sx: {
        background:
          "linear-gradient(90deg, #e0e0e0 25%, #ECE7FD 50%, #e0e0e0 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
        "@keyframes shimmer": {
          "0%": {
            backgroundPosition: "200% 0",
          },
          "100%": {
            backgroundPosition: "-200% 0",
          },
        },
      },
    },
    muiTableBodyCellProps: ({ cell, column, table }) => ({
      sx: {
        fontFamily: "Figtree-Regular",
        fontSize: "13px",
        padding: "0px 1rem",
        position: "relative",
        overflow: "visible",
        "&:focus": {
          outline: "none",
        },
        "&:hover": {
          fontFamily: "Figtree-Medium",
          fontWeight: "bold",
        },
        ...(column.id === "mrt-row-expand" && {
          maxWidth: "40px",
          width: "40px",
          minWidth: "40px",
          padding: "0px 0.5rem",
        }),
      },
    }),
    renderEmptyRowsFallback: () => {
      return scopeLoading ? (
        <div className="d-flex justify-content-center">
          <Spin />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex-grow flex items-center justify-center p-4">
          <div className="h-full flex flex-col items-center justify-center min-h-[400px] p-8">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="65"
              height="64"
              viewBox="0 0 65 64"
              fill="none"
              className="mb-4"
            >
              <path
                d="M6.9002 21.6008C6.9002 16.7407 10.8401 12.8008 15.7002 12.8008H49.3002C54.1603 12.8008 58.1002 16.7407 58.1002 21.6008V42.4008C58.1002 47.2609 54.1603 51.2008 49.3002 51.2008H32.8884L27.8968 46.2092C28.7964 44.3293 29.3002 42.2239 29.3002 40.0008C29.3002 32.0479 22.8531 25.6008 14.9002 25.6008C11.94 25.6008 9.18835 26.494 6.9002 28.0257V21.6008ZM14.9002 51.2008C17.416 51.2008 19.7381 50.3713 21.6079 48.9708L29.7688 57.132C30.3936 57.7569 31.4067 57.7569 32.0315 57.1321C32.6564 56.5072 32.6564 55.4942 32.0316 54.8693L23.8706 46.708C25.2708 44.8384 26.1002 42.5164 26.1002 40.0008C26.1002 33.8152 21.0858 28.8008 14.9002 28.8008C8.71461 28.8008 3.7002 33.8152 3.7002 40.0008C3.7002 46.1864 8.71461 51.2008 14.9002 51.2008ZM14.9002 48.0008C10.4819 48.0008 6.9002 44.4191 6.9002 40.0008C6.9002 35.5825 10.4819 32.0008 14.9002 32.0008C19.3185 32.0008 22.9002 35.5825 22.9002 40.0008C22.9002 44.4191 19.3185 48.0008 14.9002 48.0008Z"
                fill="#342C82"
              />
            </svg>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              No data currently available!
            </h3>
            <p className="text-gray-600 text-center max-w-md">
              New entries will appear here once created.
            </p>
          </div>
        </div>
      ) : null;
    },
    muiLinearProgressProps: {
      sx: {
        height: "4px",
        backgroundColor: "#cfc9ff",
        "& .MuiLinearProgress-bar": {
          backgroundColor: "#342c82",
        },
        "& .MuiLinearProgress-bar1Indeterminate": {
          backgroundColor: "#342c82",
        },
        "& .MuiLinearProgress-bar2Indeterminate": {
          backgroundColor: "#342c82",
        },
        "& .MuiLinearProgress-barColorPrimary": {
          backgroundColor: "#342c82",
        },
        "& .MuiLinearProgress-colorPrimary": {
          backgroundColor: "#cfc9ff",
        },
        "& .MuiLinearProgress-colorSecondary": {
          backgroundColor: "#cfc9ff",
        },
        "& .MuiLinearProgress-barColorSecondary": {
          backgroundColor: "#342c82",
        },
      },
    },
  });

  return <MaterialReactTable table={table} />;
};

export default ScopeTable;
